import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Icons } from "../lib/icons";
import { Btn, Pill, Modal } from "../lib/primitives";
import { useOperatorRole } from "../lib/hooks";
import {
  apiClient,
  ApiError,
  type TeamInvitationRow,
  type TeamMemberRow,
  type TeamRole,
} from "../lib/api";

const ROLE_OPTIONS: TeamRole[] = ["owner", "admin", "viewer"];

function roleLabel(role: TeamRole): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function initialsOf(name: string, email: string): string {
  const source = (name || email || "").trim();
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const second = parts[1]?.[0] ?? "";
  return (first + second).toUpperCase();
}

function colorFor(seed: string): string {
  // Deterministic-ish avatar colour from email/userId. Avoids importing a
  // hashing lib — sum char codes is good enough for a stable pastel.
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 70%, 80%)`;
}

export function Team() {
  const queryClient = useQueryClient();
  // Mirror the server's RBAC in the UI (#51): viewers see no write
  // controls, admins can't touch owners or grant the owner role. The
  // backend still enforces all of it.
  const { canWrite, isOwner } = useOperatorRole();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<TeamRole>("admin");
  const [error, setError] = useState<string | null>(null);

  const membersQuery = useQuery({
    queryKey: ["team", "members"],
    queryFn: () => apiClient.team.members(),
  });
  const invitationsQuery = useQuery({
    queryKey: ["team", "invitations"],
    queryFn: () => apiClient.team.invitations(),
    // viewer role gets 403 here; swallow so the page still renders.
    retry: false,
  });

  const inviteMutation = useMutation({
    mutationFn: () =>
      apiClient.team.invite({ email: inviteEmail.trim(), role: inviteRole }),
    onSuccess: async () => {
      setInviteOpen(false);
      setInviteEmail("");
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["team", "invitations"] }),
        queryClient.invalidateQueries({ queryKey: ["team", "members"] }),
      ]);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Couldn't send the invite. Try again.");
    },
  });

  const changeRoleMutation = useMutation({
    mutationFn: (vars: { userId: string; role: TeamRole }) =>
      apiClient.team.changeRole(vars.userId, vars.role),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["team", "members"] }),
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Couldn't change the role. Try again.");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => apiClient.team.remove(userId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["team", "members"] }),
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Couldn't remove them. Try again.");
    },
  });

  const revokeInviteMutation = useMutation({
    mutationFn: (id: string) => apiClient.team.revokeInvitation(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["team", "invitations"] }),
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Couldn't cancel the invite. Try again.");
    },
  });

  const members = membersQuery.data ?? [];
  const invitations: TeamInvitationRow[] = invitationsQuery.data ?? [];

  const stats = useMemo(() => {
    return {
      active: members.length,
      pending: invitations.length,
      total: members.length + invitations.length,
    };
  }, [members, invitations]);

  function onInvite() {
    if (!inviteEmail.trim()) return;
    setError(null);
    inviteMutation.mutate();
  }

  return (
    <>
      <div className="ad-page-head">
        <div className="ad-page-head-l">
          <div className="ad-eyebrow">Account</div>
          <h1 className="ad-h1">Team</h1>
          <div className="ad-sub">
            The people who can see and change AuthenSee for this organization.
          </div>
        </div>
        {canWrite && (
          <div className="ad-page-head-r">
            <Btn
              kind="primary"
              leading={<Icons.Plus size={14} />}
              onClick={() => setInviteOpen(true)}
            >
              Invite member
            </Btn>
          </div>
        )}
      </div>

      <div className="ad-grid-3" style={{ gap: 14, marginBottom: 18 }}>
        <MiniStat label="Members" value={stats.active.toString()} sub="have signed in" />
        <MiniStat label="Pending" value={stats.pending.toString()} sub="invite sent" />
        <MiniStat label="Total" value={stats.total.toString()} sub="incl. pending" />
      </div>

      {error && (
        <div
          style={{
            marginBottom: 14,
            padding: 10,
            borderRadius: 8,
            fontSize: 13,
            background: "rgba(220, 38, 38, 0.08)",
            color: "#b91c1c",
          }}
        >
          {error}
        </div>
      )}

      <div className="ad-card-flat" style={{ padding: 0, overflow: "hidden" }}>
        <table className="ad-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last seen</th>
              <th style={{ textAlign: "right", width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {membersQuery.isLoading && (
              <tr>
                <td colSpan={5} className="muted" style={{ fontSize: 12 }}>
                  Loading…
                </td>
              </tr>
            )}
            {!membersQuery.isLoading && members.length === 0 && (
              <tr>
                <td colSpan={5} className="muted" style={{ fontSize: 12 }}>
                  No members yet.
                </td>
              </tr>
            )}
            {members.map((m: TeamMemberRow) => (
              <tr key={m.userId}>
                <td>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 999,
                        background: colorFor(m.email),
                        color: "#000",
                        display: "grid",
                        placeItems: "center",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 0.05,
                      }}
                    >
                      {initialsOf(m.name, m.email)}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {m.name || m.email}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--ad-fg-3)" }}>
                        {m.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  {canWrite && (isOwner || m.role !== "owner") ? (
                    <select
                      className="ad-input"
                      value={m.role}
                      onChange={(e) =>
                        changeRoleMutation.mutate({
                          userId: m.userId,
                          role: e.target.value as TeamRole,
                        })
                      }
                      disabled={changeRoleMutation.isPending}
                      style={{ width: 110, padding: "5px 8px", fontSize: 12 }}
                    >
                      {ROLE_OPTIONS.filter((r) => isOwner || r !== "owner").map((r) => (
                        <option key={r} value={r}>
                          {roleLabel(r)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ fontSize: 13 }}>{roleLabel(m.role)}</span>
                  )}
                </td>
                <td>
                  <Pill kind="success" dot>
                    Active
                  </Pill>
                </td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {m.lastSeen
                    ? new Date(m.lastSeen).toLocaleDateString()
                    : "–"}
                </td>
                <td>
                  {members.length > 1 && canWrite && (isOwner || m.role !== "owner") ? (
                    <button
                      className="ad-top-icon-btn"
                      style={{ width: 28, height: 28 }}
                      title="Remove member"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove ${m.email}? They will lose access immediately.`,
                          )
                        ) {
                          removeMutation.mutate(m.userId);
                        }
                      }}
                    >
                      <Icons.Trash size={14} />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invitations.length > 0 && (
        <>
          <div style={{ height: 18 }} />
          <div className="ad-card">
            <div className="ad-card-head">
              <div className="ad-card-title">Pending invitations</div>
            </div>
            <table className="ad-table" style={{ marginTop: -8 }}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Invited by</th>
                  <th>Sent</th>
                  <th>Expires</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => (
                  <tr key={inv.id}>
                    <td style={{ fontSize: 13 }}>{inv.email}</td>
                    <td style={{ fontSize: 13 }}>{roleLabel(inv.role)}</td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {inv.invitedByEmail ?? "–"}
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {new Date(inv.createdAt).toLocaleDateString()}
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {new Date(inv.expiresAt).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Btn
                        kind="ghost"
                        size="sm"
                        onClick={() => revokeInviteMutation.mutate(inv.id)}
                        disabled={revokeInviteMutation.isPending}
                      >
                        Revoke
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div style={{ height: 18 }} />

      <div className="ad-card">
        <div className="ad-card-head">
          <div className="ad-card-title">What each role can do</div>
        </div>
        <table className="ad-table" style={{ marginTop: -8 }}>
          <thead>
            <tr>
              <th>Role</th>
              <th>See everything</th>
              <th>Change settings</th>
              <th>API keys</th>
              <th>Team</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Owner", "✓", "✓", "✓", "✓"],
              ["Admin", "✓", "✓", "✓", "–"],
              ["Viewer", "✓", "–", "–", "–"],
            ].map(([r, ...cells]) => (
              <tr key={r}>
                <td style={{ fontWeight: 500 }}>{r}</td>
                {cells.map((c, i) => (
                  <td
                    key={i}
                    style={{
                      color:
                        c === "✓" ? "var(--ad-success)" : "var(--ad-fg-4)",
                      fontWeight: 600,
                    }}
                  >
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        eyebrow="Team"
        title="Invite someone"
        footer={
          <>
            <Btn kind="ghost" onClick={() => setInviteOpen(false)}>
              Cancel
            </Btn>
            <Btn
              kind="primary"
              leading={<Icons.Mail size={14} />}
              onClick={onInvite}
              disabled={inviteMutation.isPending || !inviteEmail.trim()}
            >
              {inviteMutation.isPending ? "Sending…" : "Send invite"}
            </Btn>
          </>
        }
      >
        <div className="ad-stack-lg">
          <div className="ad-field">
            <div className="ad-field-label">Email</div>
            <input
              className="ad-input"
              placeholder="teammate@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              autoFocus
            />
          </div>
          <div className="ad-field">
            <div className="ad-field-label">Role</div>
            <div className="ad-grid-2">
              {ROLE_OPTIONS.filter((r) => isOwner || r !== "owner").map((r) => (
                <div
                  key={r}
                  className="ad-radio-card"
                  data-checked={inviteRole === r}
                  onClick={() => setInviteRole(r)}
                >
                  <div className="ad-radio-card-head">
                    <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ad-fg-1)" }}>
                      {roleLabel(r)}
                    </div>
                    <div className="ad-radio-card-dot" />
                  </div>
                  <div style={{ fontSize: 11, color: "var(--ad-fg-3)" }}>
                    {r === "owner" &&
                      "Full access. Can invite other owners and remove members."}
                    {r === "admin" &&
                      "Everything except adding or removing owners."}
                    {r === "viewer" &&
                      "Can look but not change anything. Good for audits."}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

function MiniStat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="ad-card" style={{ padding: 16 }}>
      <div className="ad-eyebrow-sm">{label}</div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 500,
          letterSpacing: "-0.02em",
          margin: "6px 0 2px",
          fontVariantNumeric: "tabular-nums",
          color: "var(--ad-fg-1)",
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 11, color: "var(--ad-fg-3)" }}>{sub}</div>
    </div>
  );
}
